import { Injectable, inject, signal, computed } from '@angular/core';
import { OverlayService } from './overlay.service';
import { AnnouncerService, AnnouncerPoliteness } from './announcer.service';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: number;
  message: string;
  type: ToastType;
  duration?: number;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly overlayService = inject(OverlayService);
  private readonly announcer = inject(AnnouncerService);

  private nextId = 1;
  readonly toasts = signal<Toast[]>([]);

  readonly hasToasts = computed(() => this.toasts().length > 0);

  show(message: string, type: ToastType = 'info', duration = 4000): number {
    const id = this.nextId++;
    const toast: Toast = { id, message, type, duration };
    this.toasts.update((t) => [...t, toast]);

    // Announce to screen readers
    const politeness: AnnouncerPoliteness = type === 'error' ? 'assertive' : 'polite';
    this.announcer.announce(message, politeness);

    // Auto-dismiss
    if (duration > 0) {
      setTimeout(() => this.dismiss(id), duration);
    }

    return id;
  }

  success(message: string, duration = 4000): number {
    return this.show(message, 'success', duration);
  }

  error(message: string, duration = 6000): number {
    return this.show(message, 'error', duration);
  }

  info(message: string, duration = 4000): number {
    return this.show(message, 'info', duration);
  }

  warning(message: string, duration = 5000): number {
    return this.show(message, 'warning', duration);
  }

  dismiss(id: number): void {
    this.toasts.update((t) => t.filter((toast) => toast.id !== id));
  }

  dismissAll(): void {
    this.toasts.set([]);
  }
}